'use client'

import { isEmpty } from 'es-toolkit/compat'
import { useCallback } from 'react'
import { useInView } from 'react-intersection-observer'

import {
  ADDRESS_SEARCH_EMPTY_DATA_TEXT,
  DEFAULT_RESULT_LIST_ROOT_MARGIN,
  DEFAULT_RESULT_LIST_THRESHOLD,
  GEO_LOCATION_ACCESS_DENIED_RESULT,
  type IAddressDocument,
  useCurrentLocationStore,
} from '@/entities/place'
import { EmptyData } from '@/features/seoPlaceSearch/ui/EmptyData'
import { IconAlertTriangleYellow } from '@/shared/assets'
import { CircularProgressIndicator } from '@/shared/design-system/components'
import { ListRow } from '@/shared/design-system/components/ListRow'
import {
  COMMON_ERROR_DESCRIPTION,
  COMMON_ERROR_TITLE,
  getBrowserType,
} from '@/shared/lib'
import { Result } from '@/shared/ui'

const HighlightText = ({
  text,
  keyword,
  position,
}: {
  text: string
  keyword: string
  position: 'title' | 'lower'
}) => {
  // 키워드를 단어별로 분할 (예: "강남구 테헤란로" => ["강남구", "테헤란로"])
  const keywordWords = keyword
    .trim() // 앞뒤 공백 제거
    .split(/\s+/) // 공백으로 단어 분할 (여러 공백도 하나로 처리)
    .filter(word => word.length > 0) // 빈 문자열 제거

  // 텍스트를 단어별로 분할 (예: "강남구 테헤란로" => ["강남구", " ", "테헤란로"])
  const textWords = text.split(/(\s+)/)

  return (
    <span
      className={`no-underline ${position === 'title' ? 'text-title-2-semibold text-foreground-neutral-strong' : 'text-body-2-regular text-foreground-neutral-normal'}`}
    >
      {textWords.map((word, index) => {
        // 공백인 경우 그대로 표시
        if (word.trim() === '') {
          return <span key={index}>{word}</span>
        }

        const lowerCaseKeyWord = word.toLowerCase()

        // 키워드 단어들과 매칭되는지 확인 (정확히 일치하거나 앞부분이 일치)
        const matchingKeyword = keywordWords.find(
          keywordWord =>
            lowerCaseKeyWord === keywordWord.toLowerCase() ||
            lowerCaseKeyWord.startsWith(keywordWord.toLowerCase())
        )

        return (
          <span key={index}>
            {matchingKeyword ? (
              <span>
                <span className='text-foreground-brand-strong'>
                  {matchingKeyword}
                </span>
                {word.length > matchingKeyword.length && (
                  <span>{word.slice(matchingKeyword.length)}</span>
                )}
              </span>
            ) : (
              word
            )}
          </span>
        )
      })}
    </span>
  )
}

const AddressSearchList = ({
  searchResults,
  keyword,
  hasMoreAddresses,
  hasSearchError,
  loadMoreAddresses,
  retryAddressSearch,
  isLoadingAddressList,
  isLoadingMoreAddresses,
  onClickAddressItem,
  onCloseBottomSheet,
}: {
  searchResults: IAddressDocument[] | null
  keyword: string
  hasMoreAddresses: boolean
  hasSearchError: boolean
  loadMoreAddresses: () => void
  retryAddressSearch: () => void
  isLoadingMoreAddresses: boolean
  isLoadingAddressList: boolean
  onClickAddressItem: (address: IAddressDocument) => void
  onCloseBottomSheet: () => void
}) => {
  const isAccessDenied = useCurrentLocationStore().get('isAccessDenied')

  const handleInViewChange = useCallback(
    (inView: boolean) => {
      if (hasSearchError) return

      const hasMorePage = hasMoreAddresses && !isLoadingMoreAddresses
      if (inView && hasMorePage) {
        loadMoreAddresses()
      }
    },
    [
      loadMoreAddresses,
      hasMoreAddresses,
      isLoadingMoreAddresses,
      hasSearchError,
    ]
  )

  const { ref } = useInView({
    threshold: DEFAULT_RESULT_LIST_THRESHOLD,
    rootMargin: DEFAULT_RESULT_LIST_ROOT_MARGIN,
    onChange: handleInViewChange,
    skip: !hasMoreAddresses || hasSearchError,
  })

  if (isAccessDenied) {
    return (
      <Result
        icon={<IconAlertTriangleYellow width='48' height='48' />}
        title={GEO_LOCATION_ACCESS_DENIED_RESULT.TITLE}
        description={
          getBrowserType() === 'chrome'
            ? GEO_LOCATION_ACCESS_DENIED_RESULT.DESCRIPTION.CHROME
            : getBrowserType() === 'safari'
              ? GEO_LOCATION_ACCESS_DENIED_RESULT.DESCRIPTION.SAFARI
              : GEO_LOCATION_ACCESS_DENIED_RESULT.DESCRIPTION.DEFAULT
        }
      />
    )
  }

  // 초기 로딩
  if (isLoadingAddressList) {
    return <CircularProgressIndicator size='large' variant='gray' />
  }

  // 검색 전
  if (searchResults === null) {
    // 검색 에러
    if (hasSearchError) {
      return (
        <Result
          icon={<IconAlertTriangleYellow width='48' height='48' />}
          title={COMMON_ERROR_TITLE}
          description={COMMON_ERROR_DESCRIPTION}
          handleRetry={retryAddressSearch}
        />
      )
    }
    return null
  }

  // 검색 결과가 없을 때
  if (!isLoadingAddressList && searchResults.length === 0) {
    return <EmptyData text={ADDRESS_SEARCH_EMPTY_DATA_TEXT} />
  }

  return (
    <>
      <div>
        {searchResults.map(item => (
          <ListRow
            key={item.id}
            title={
              <div>
                <HighlightText
                  text={item.place_name}
                  keyword={keyword}
                  position='title'
                />
              </div>
            }
            lower={
              <div className='pt-dimension-0-5'>
                <HighlightText
                  text={
                    isEmpty(item.road_address_name)
                      ? item.address_name
                      : item.road_address_name
                  }
                  keyword={keyword}
                  position='lower'
                />
              </div>
            }
            handleClick={() => {
              onClickAddressItem(item)
              onCloseBottomSheet()
            }}
          />
        ))}

        {isLoadingMoreAddresses && (
          <CircularProgressIndicator size='medium' variant='gray' />
        )}

        {hasSearchError && (
          <Result
            title={COMMON_ERROR_TITLE}
            description={COMMON_ERROR_DESCRIPTION}
            handleRetry={retryAddressSearch}
          />
        )}
      </div>

      {hasMoreAddresses && !hasSearchError && <div ref={ref} />}
    </>
  )
}

export default AddressSearchList
